import { Anchor, Breadcrumbs, Text } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import AiDraftNotice from '../../components/AiDrafts/AiDraftNotice';
import SenderVerificationAlert from '../../components/SenderVerificationAlert';
import ErrorMessage from '../../components/ErrorMessage';
import Metadata from '../../components/Metadata';
import Spinner from '../../components/Spinner';
import WorkflowForm from '../../components/Workflows/WorkflowForm';
import { createFlowNode, generateEdgeId, getTypeOfFlow } from '../../constants/flowTypes';
import { CampaignTypes, Page } from '../../constants/pageTitles';
import { normalizeCampaignGoals } from '../../helpers/campaignGoals';
import { friendlyGoogleErrorMessage } from '../../helpers/googleErrorUtils';
import useFetch from '../../helpers/useFetch';

const page = `${Page['workflows']}`;

const WorkflowsDetailsPage = () => {
  const navigate = useNavigate();
  const fetch = useFetch();
  const location = useLocation();
  const [userError, setUserError] = useState();
  const [loadError, setLoadError] = useState();
  const [campaign, setCampaign] = useState();
  const { accountId, domainId, campaignId } = useParams();

  const queryParams = new URLSearchParams(location.search);
  const flowType = queryParams.get('flowType');
  const flowElementId = parseInt(queryParams.get('flowElementId'));
  const flowElementName = queryParams.get('flowElementName');

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/campaigns/${campaignId}`)
      .then(({ campaign }) => {
        if (flowElementId && flowType && flowElementName && campaign) {
          const nodes = campaign.flow.nodes;
          const edges = campaign.flow.edges;
          const outgoingEdgesMap = new Map();
          for (const edge of edges) {
            const sourceNodeId = edge.source;
            if (!outgoingEdgesMap.has(sourceNodeId)) {
              outgoingEdgesMap.set(sourceNodeId, []);
            }
            outgoingEdgesMap.get(sourceNodeId).push(edge);
          }
          let lastNode = null;
          for (const node of nodes) {
            const nodeId = node.id;
            if (!outgoingEdgesMap.has(nodeId)) {
              lastNode = node;
            }
          }

          const newFlowNode = createFlowNode(flowType, flowElementId, flowElementName, {
            x: 276,
            y: lastNode.position.y + 200,
          });

          campaign.flow.edges.push({
            id: generateEdgeId(lastNode.id, getTypeOfFlow(flowType) + flowElementId),
            source: lastNode.id,
            target: getTypeOfFlow(flowType) + flowElementId,
          });
          campaign.flow.nodes.push(newFlowNode);
        }

        if (!campaign.googleAssetGroup) {
          campaign.googleAssetGroup = {};
        }

        setCampaign(normalizeCampaignGoals(campaign));
      })
      .catch((error) => {
        setLoadError(error);
      });
  }, [fetch, accountId, domainId, campaignId, flowElementId, flowType, flowElementName]);

  const update = (campaign) => {
    setUserError(undefined);
    return fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/campaigns/${campaignId}`, {
      method: 'PUT',
      body: JSON.stringify({ ...campaign, running: undefined }),
    })
      .then(() => {
        navigate(location.pathname.replace(`/workflows/${campaign.id}/details`, `/workflows?mode=${campaign.mode}`));
      })
      .catch((error) => {
        setUserError({ message: friendlyGoogleErrorMessage(error?.message) });
        fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/campaigns/${campaignId}`)
          .then(({ campaign }) => setCampaign(normalizeCampaignGoals(campaign)))
          .catch(() => {});
      });
  };

  const del = (cb) => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/campaigns/${campaign.id}`, {
      method: 'DELETE',
    })
      .then(() => {
        if (typeof cb === 'function') {
          cb();
        }
        navigate(location.pathname.replace(`/workflows/${campaign.id}/details`, `/workflows?mode=${campaign.mode}`));
      })
      .catch(setLoadError);
  };

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  if (campaign === undefined) {
    return <Spinner />;
  }

  const enitityLabel = campaign.name;

  const breadcrumbItems = [
    { title: 'Home', href: `#/accounts/${accountId}/domains/${domainId}/analytics?mode=${campaign.mode}` },
    { title: page, href: `#/accounts/${accountId}/domains/${domainId}/workflows?mode=${campaign.mode}` },
    { title: enitityLabel, href: `#${location.pathname.replace('details', 'analytics')}?mode=${campaign.mode}` },
    { title: 'Edit' },
  ].map((item, index) =>
    item.href ? (
      <Anchor href={item.href} key={index}>
        {item.title}
      </Anchor>
    ) : (
      <Text key={index}>{item.title}</Text>
    )
  );

  return (
    <div data-tour="workflowDetails">
      <Metadata title={`${page} | ${CampaignTypes[campaign.mode]} | ${campaign.name} | Edit`} />
      <Breadcrumbs mb="md">{breadcrumbItems}</Breadcrumbs>

      <SenderVerificationAlert />
      {campaign.aiReviewStatus === 'pending' && <AiDraftNotice />}
      <WorkflowForm campaign={campaign} saveCampaign={update} deleteCampaign={(cb) => del(cb)} />
      {userError && (
        <div style={{ marginLeft: '50%' }}>
          <ErrorMessage message={userError.message} style={{ whiteSpace: 'pre-line', lineHeight: '1' }} />
        </div>
      )}
    </div>
  );
};

export default WorkflowsDetailsPage;
